import { useState } from 'react';
import MainHeader from '@/components/MainHeader';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Phone, Mail, MapPin, Wrench, Clock, Shield, Award, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import assistenzaImg from '@/assets/assistenza.png';
import SEOHead from '@/components/SEOHead';
import { ServiceSchema, BreadcrumbSchema } from '@/components/SchemaOrg';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const Assistenza = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    azienda: '',
    nome: '',
    cognome: '',
    citta: '',
    telefono: '',
    email: '',
    prodotto: '',
    marca: '',
    descrizione: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-assistance-request', {
        body: formData,
      });

      if (error) throw error;

      toast.success('Richiesta inviata con successo! Ti ricontatteremo al più presto.');
      setFormData({
        azienda: '',
        nome: '',
        cognome: '',
        citta: '',
        telefono: '',
        email: '',
        prodotto: '',
        marca: '',
        descrizione: '',
      });
    } catch (error: any) {
      console.error('Error submitting form:', error);
      toast.error('Errore nell\'invio della richiesta. Riprova più tardi.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <>
      <SEOHead
        title="Assistenza Tecnica Stampanti DTF e DTF UV | Tecnici Certificati | Materialidtf.it"
        description="Assistenza tecnica professionale per stampanti DTF e DTF UV. Tecnici qualificati, intervento rapido, ricambi originali. Manutenzione, riparazione e supporto remoto."
        canonical="/assistenza"
        keywords="assistenza stampanti DTF, riparazione stampanti DTF, manutenzione DTF, tecnici DTF certificati, supporto stampanti UV, assistenza tecnica stampa digitale"
      />
      <ServiceSchema
        name="Assistenza Tecnica Stampanti DTF"
        description="Servizio di assistenza tecnica professionale per stampanti DTF e DTF UV. Interventi rapidi, ricambi originali e tecnici certificati."
        url="/assistenza"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Assistenza Tecnica', url: '/assistenza' }
        ]}
      />

      <MainHeader />
      <Navigation />

      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="bg-primary py-16 px-4">
          <div className="container mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4">
              Assistenza Tecnica Professionale
            </h1>
            <p className="text-xl text-primary-foreground/90 max-w-2xl mx-auto">
              Supporto specializzato per le tue stampanti DTF e DTF UV
            </p>
          </div>
        </section>

        {/* Image Section */}
        <section className="py-12 px-4 bg-muted">
          <div className="container mx-auto flex justify-center">
            <img 
              src={assistenzaImg} 
              alt="Servizio assistenza tecnica professionale per stampanti DTF" 
              className="max-w-full md:max-w-2xl rounded-lg shadow-lg"
              loading="lazy"
            />
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 px-4">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-center text-foreground mb-12">
              Perché Scegliere la Nostra Assistenza
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center p-6 bg-card rounded-lg shadow-md">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">Tecnici Qualificati</h3>
                <p className="text-muted-foreground">
                  Il nostro team è composto da tecnici specializzati e certificati con anni di esperienza nel settore DTF e DTF UV.
                </p>
              </div>
              <div className="text-center p-6 bg-card rounded-lg shadow-md">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">Intervento Rapido</h3>
                <p className="text-muted-foreground">
                  Garantiamo tempi di risposta rapidi per minimizzare i tempi di fermo della tua produzione.
                </p>
              </div>
              <div className="text-center p-6 bg-card rounded-lg shadow-md">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Wrench className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">Ricambi Originali</h3>
                <p className="text-muted-foreground">
                  Utilizziamo esclusivamente ricambi originali per garantire la massima affidabilità delle riparazioni.
                </p>
              </div>
              <div className="text-center p-6 bg-card rounded-lg shadow-md">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">Garanzia di Qualità</h3>
                <p className="text-muted-foreground">
                  Tutti i nostri interventi sono garantiti e seguiamo i più alti standard di qualità del settore.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 px-4 bg-muted">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-center text-foreground mb-4">
              Tecnici Specializzati al Tuo Servizio
            </h2>
            <p className="text-center text-muted-foreground max-w-3xl mx-auto mb-12">
              Il nostro team di assistenza tecnica è composto da professionisti altamente qualificati, costantemente aggiornati sulle ultime tecnologie DTF, DTF UV e UV.
            </p>
            <div className="max-w-2xl mx-auto">
              <h3 className="text-xl font-semibold text-foreground mb-6">Offriamo supporto completo per:</h3>
              <ul className="space-y-4">
                {[
                  'Installazione e configurazione stampanti',
                  'Manutenzione programmata e preventiva',
                  'Riparazione guasti e sostituzione ricambi',
                  'Ottimizzazione delle performance di stampa',
                  'Formazione e training per il personale',
                  'Assistenza remota e on-site'
                ].map((service, index) => (
                  <li key={index} className="flex items-center gap-3 text-foreground">
                    <div className="w-2 h-2 bg-primary rounded-full" />
                    {service}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Contact & Form Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-8">Contatti Diretti</h2>
                <div className="space-y-6">
                  <a href="tel:+393757190556" className="flex items-center gap-4 text-foreground hover:text-primary transition-colors">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <Phone className="w-6 h-6 text-primary" />
                    </div>
                    <span className="text-lg">+39 375 7190556</span>
                  </a>
                  <a href="mailto:assistenza@materialidtf.it" className="flex items-center gap-4 text-foreground hover:text-primary transition-colors">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <Mail className="w-6 h-6 text-primary" />
                    </div>
                    <span className="text-lg">assistenza@materialidtf.it</span>
                  </a>
                  <div className="flex items-center gap-4 text-foreground">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-primary" />
                    </div>
                    <span className="text-lg">Via Don Minzoni 15, 00013 Fonte Nuova (RM), Italia</span>
                  </div>
                </div>
              </div>

              {/* Request Form */}
              <div className="bg-card p-8 rounded-lg shadow-lg">
                <h2 className="text-2xl font-bold text-foreground mb-2">Richiedi Assistenza</h2>
                <p className="text-muted-foreground mb-6">Compila il form e ti ricontatteremo nel più breve tempo possibile</p>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="azienda">Azienda *</Label>
                      <Input id="azienda" value={formData.azienda} onChange={handleInputChange} required />
                    </div>
                    <div>
                      <Label htmlFor="nome">Nome *</Label>
                      <Input id="nome" value={formData.nome} onChange={handleInputChange} required />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="cognome">Cognome *</Label>
                      <Input id="cognome" value={formData.cognome} onChange={handleInputChange} required />
                    </div>
                    <div>
                      <Label htmlFor="citta">Città *</Label>
                      <Input id="citta" value={formData.citta} onChange={handleInputChange} required />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="telefono">Telefono *</Label>
                      <Input id="telefono" type="tel" value={formData.telefono} onChange={handleInputChange} required />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" type="email" value={formData.email} onChange={handleInputChange} required />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="prodotto">Prodotto *</Label>
                      <Input id="prodotto" value={formData.prodotto} onChange={handleInputChange} required />
                    </div>
                    <div>
                      <Label htmlFor="marca">Marca *</Label>
                      <Input id="marca" value={formData.marca} onChange={handleInputChange} required />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="descrizione">Descrizione Problema *</Label>
                    <Textarea id="descrizione" rows={4} value={formData.descrizione} onChange={handleInputChange} required />
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Invio in corso...
                      </>
                    ) : (
                      'Invia Richiesta'
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Assistenza;
